import React, { useEffect, useRef } from "react"
import tw from "twin.macro"
import { useTealiumContext } from "../../../contexts/Tealium"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import { ExternalVideoProps } from "./Video.d"
import useComponentId from "../../../hooks/useIdGenerator"

interface CustomHTMLIFrameElement extends HTMLIFrameElement {
  destroy: () => void
}

const ExternalVideo: React.FC<ExternalVideoProps> = ({
  videoSectionHeading,
  videoSectionVideo,
  autoPlay = false,
  videoCss,
  extraTealiumValues,
  ...remainingProps
}) => {
  const uuid = useComponentId()
  // Tealium for video events using YouTube iFrame API
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useTealiumContext()
  const videoId = videoSectionVideo?.embedUrl?.split("/").pop()
  const playerRef = useRef<CustomHTMLIFrameElement | null>(null)
  const pauseDueToFocusLossRef = useRef(false) // Flag to track focus-related pause events

  // Store the latest tealPageData and extraTealiumValues in a ref
  const tealPageDataRef = useRef(tealPageData)
  const extraTealiumValuesRef = useRef(extraTealiumValues)

  // Update refs when tealPageData and extraTealiumValues changes
  useEffect(() => {
    tealPageDataRef.current = tealPageData
    extraTealiumValuesRef.current = extraTealiumValues
  }, [tealPageData, extraTealiumValuesRef])

  function onPlayerReady(event: {
    target: {
      mute: () => void
      loadModule: (arg0: string) => void
      playVideo: () => void
    }
  }) {
    event.target.mute()
    event.target.loadModule("captions") //Works for html5 ignored by AS3
    event.target.loadModule("cc") //Works for AS3 ignored by html5
    setTimeout(function () {
      event.target.playVideo()
    }, 0)
  }

  const getTealiumData = () => {
    // First try to use the latest data from our ref
    if (tealPageDataRef.current && tealPageDataRef.current.page_name) {
      return {
        ...tealPageDataRef.current,
        ...(extraTealiumValuesRef.current && extraTealiumValuesRef.current), // Include extraTealiumValuesRef.current
      }
    }

    // Fall back to window.utag_data if available
    if (window.utag_data && window.utag_data.page_name) {
      return {
        ...window.utag_data,
        ...(extraTealiumValuesRef.current && extraTealiumValuesRef.current), // Include extraTealiumValuesRef.current
      }
    }

    // Try parent window's utag_data
    try {
      if (
        window.parent &&
        window.parent.utag_data &&
        window.parent.utag_data.page_name
      ) {
        return {
          ...window.parent.utag_data,
          ...(extraTealiumValuesRef.current && extraTealiumValuesRef.current), // Include extraTealiumValuesRef.current
        }
      }
    } catch (e) {
      console.log("Error accessing parent window data:", e)
    }

    // Final fallback - try to access teal object if it exists
    try {
      if (window.parent && window.parent.teal) {
        return {
          ...window.parent.teal,
          ...(extraTealiumValuesRef.current && extraTealiumValuesRef.current), // Include extraTealiumValuesRef.current
        }
      }
    } catch (e) {
      console.log("Error accessing parent window teal object:", e)
    }

    // Return empty object as last resort
    return {}
  }

  const handleTealiumEvent = (
    action: string,
    variable: string,
    title?: string,
  ) => {
    // Always get fresh data when the event fires
    const tealData = getTealiumData()
    trackTealEvent({
      // Only include these fields if we have data to provide
      ...(Object.keys(tealData).length > 0 && {
        ...tealData,
      }),
      tealium_event: "video_event",
      video_type: `${
        videoSectionHeading || title
      }-${videoId}|${action}|${variable}`,
    })
  }

  const onPlayerStateChange = (event: { data: any; target: any }) => {
    const isFocused = document.hasFocus() // Check if the browser is focused
    const currentTime = Math.round(event.target.getCurrentTime()).toString()
    const title = event.target.getVideoData().title

    if (event.data == 2) {
      // Pause event
      if (!isFocused) {
        console.log("Pause due to focus loss. Ignoring event.")
        pauseDueToFocusLossRef.current = true // Mark that the pause was due to focus loss
        return
      }
      const currentPercent = Math.round(
        (parseInt(currentTime) / Math.round(event.target.getDuration())) * 100,
      ).toString()
      handleTealiumEvent("pause", `${currentPercent}:${currentTime}`, title)
    }

    if (event.data == 1) {
      // Start event
      if (pauseDueToFocusLossRef.current) {
        console.log("Start event after focus loss. Ignoring event.")
        pauseDueToFocusLossRef.current = false // Reset the flag
        return
      }
      if (autoPlay && currentTime === "0") {
        console.log("Start event due to autoplay. Ignoring event.")
        return
      }
      const currentPercent = Math.round(
        (parseInt(currentTime) / Math.round(event.target.getDuration())) * 100,
      ).toString()

      handleTealiumEvent("start", `${currentPercent}:${currentTime}`, title)
    }

    if (event.data == 0) {
      // Ended event
      handleTealiumEvent("ended", `100:${currentTime}`, title)
    }
  }

  // Create player object and generate iFrame
  useEffect(() => {
    try {
      window.YT.ready(function () {
        playerRef.current = new window.YT.Player(`${uuid}`, {
          videoId: videoId,
          allow: "autoplay; encrypted-media",
          title: "video",
          host: "https://www.youtube.com",
          playerVars: {
            playlsinline: 1,
            mute: autoPlay ? 1 : 0,
            autoplay: autoPlay ? 1 : 0,
          },
          enablejsapi: 1,
          events: {
            onReady: autoPlay ? onPlayerReady : undefined,
            onStateChange: onPlayerStateChange,
          },
          modestbranding: 1,
          origin: window.location.href,
        })
      })
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.log("Error destroying player:", e.message)
      } else {
        console.log("Error destroying player:", e)
      }
    }

    // Cleanup function
    return () => {
      if (
        playerRef.current &&
        typeof playerRef.current.destroy === "function"
      ) {
        try {
          playerRef.current.destroy()
        } catch (e) {
          console.log("Error destroying player:", e)
        }
      }
    }
  }, [])

  return (
    <div
      id={`${uuid}`}
      css={videoCss ? videoCss : [tw`h-1/3 w-full p-12`, tw`md:(h-5/6 w-full)`]}
      {...remainingProps}
    ></div>
  )
}

export default ExternalVideo
